import { FC, useEffect, useState } from 'react';

import {
    FormField,
    Label,
    Input,
    TextArea,
    Select,
    FieldErrorList,
    DatePicker,
    Search,
    SelectOption
} from 'common/components';
import { ApiStatus, IndicatorDetailFields, InputType } from 'common/enums';
import {
    INDICATOR_TYPE_OPTIONS,
    INDICATOR_SORT_ORDER,
    INDICATOR_UPDATE_FREQUENCY,
    INDICATOR_VALUE_TYPE_OVERRIDE
} from 'common/config';
import { FieldError } from 'common/models';
import { Source } from 'common/models/data/entity.model';
import Checkbox from 'common/components/base/checkbox';

import './IndicatorDetails.scss';
import IndicatorSourceAddEdit from 'modules/data-management/components/indicator-source-add-edit/IndicatorSourceAddEdit';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'common/redux/core/root.reducer';
import { getIndicatorSourceAddResponse } from 'modules/configuration/redux/configuration.selector';
import { configurationActions } from 'modules/configuration/redux';
import { entityActions } from 'common/redux/entity';
import { removeValidationError } from 'common/utils/error';

type IndicatorDetailsProps = {
    data: any;
    fieldErrors: Array<FieldError>;
    isShowUpdateTypeAlert: boolean;
    dataCount?: number;
    isOverride: boolean;
    sourceList: Array<Source>;
    sourceSearchValue: string;
    isSetUpIndicatorSourceDisabled?: boolean;
    updateFieldErrors: (errors: Array<FieldError>) => void;
    setShowUpdateTypeAlert: (value: boolean) => void;
    setOverride: (value: boolean) => void;
    onChange: (fieldName: string, value: string) => void;
    onSourceSearchChange: (value: string) => void;
    onSourceSelection: (value: string) => void;
};

const IndicatorDetails: FC<IndicatorDetailsProps> = ({
    data,
    fieldErrors,
    isShowUpdateTypeAlert,
    dataCount,
    isOverride,
    sourceList,
    sourceSearchValue,
    isSetUpIndicatorSourceDisabled = false,
    setOverride,
    setShowUpdateTypeAlert,
    onChange,
    onSourceSearchChange,
    onSourceSelection,
    updateFieldErrors
}: IndicatorDetailsProps) => {
    const [isAddSourceModalVisible, setIsAddSourceModalVisible] = useState(false);
    const dispatch = useDispatch();

    const indicatorAddResponse = useSelector((state: RootState) =>
        getIndicatorSourceAddResponse(state)
    );

    useEffect(() => {
        let index;
        const yearList = document.getElementsByClassName(
            'react-date-picker__inputGroup__input react-date-picker__inputGroup__year'
        );
        for (index = 0; index < yearList.length; ++index) {
            yearList[index].setAttribute('readonly', '');
            yearList[index].setAttribute('disabled', '');
        }

        const dayList = document.getElementsByClassName(
            'react-date-picker__inputGroup__input react-date-picker__inputGroup__day react-date-picker__inputGroup__input--hasLeadingZero'
        );
        for (index = 0; index < dayList.length; ++index) {
            dayList[index].setAttribute('readonly', '');
            dayList[index].setAttribute('disabled', '');
        }

        const dateList = document.getElementsByClassName(
            'react-date-picker__inputGroup__input react-date-picker__inputGroup__month react-date-picker__inputGroup__input--hasLeadingZero'
        );
        for (index = 0; index < dateList.length; ++index) {
            dateList[index].setAttribute('readonly', '');
            dateList[index].setAttribute('disabled', '');
        }
    });

    useEffect(() => {
        if (indicatorAddResponse.status === ApiStatus.SUCCESS) {
            setIsAddSourceModalVisible(false);
            onSourceSearchChange(indicatorAddResponse.data?.sourceName || '');
            if (indicatorAddResponse.data) {
                dispatch(
                    entityActions.updateSourceList({
                        sourceId: indicatorAddResponse.data.sourceId,
                        sourceName: indicatorAddResponse.data.sourceName,
                        webAddress: indicatorAddResponse.data.webAddress,
                        activeStatus: true
                    })
                );
                dispatch(
                    entityActions.updateIndicatorDetails({
                        ...data,
                        sourceId: indicatorAddResponse.data.sourceId
                    })
                );
            }
            let errors = fieldErrors;
            errors = removeValidationError(errors, IndicatorDetailFields.SourceId);
            errors = removeValidationError(errors, IndicatorDetailFields.WebAddress);
            updateFieldErrors(errors);

            dispatch(configurationActions.resetIndicatorAddResponse());
        }
    }, [
        data,
        dispatch,
        fieldErrors,
        indicatorAddResponse,
        onSourceSearchChange,
        updateFieldErrors
    ]);

    const onValueTypeChange = (selectedValue: any) => {
        if (selectedValue === data.valueType) {
            setShowUpdateTypeAlert(false);
            setOverride(false);
        } else {
            setShowUpdateTypeAlert(true);
        }
        onChange(IndicatorDetailFields.ValueType, selectedValue);
    };

    const handleAddNewClose = () => {
        setIsAddSourceModalVisible(false);
    };

    const handleNewSourceAddition = () => {
        setIsAddSourceModalVisible(true);
    };

    const getSourceListOptions = () => {
        const updatedSourceList =
            sourceList
                ?.filter((el: Source) =>
                    el.sourceName.toLowerCase().includes(sourceSearchValue.toLowerCase())
                )
                .map((item: Source) => item.sourceName) || [];
        return updatedSourceList?.filter(
            (item, index) => updatedSourceList.indexOf(item) === index
        );
    };

    const webAddressOptions =
        sourceList
            ?.filter(
                (el: Source) => el.sourceName.toLowerCase() === sourceSearchValue.toLowerCase()
            )
            .map((item: Source) => ({ label: item.webAddress, value: item.sourceId })) || [];

    const selectedWebAddress = webAddressOptions?.find(
        (item: SelectOption) => item.value === data.sourceId
    );

    return (
        <>
            <div className="indicator-details">
                <FormField className="input-form-field">
                    <Label text="Name (required)"></Label>
                    <Input
                        className=""
                        type={InputType.TEXT}
                        value={data.name}
                        onChange={(event) => {
                            onChange(IndicatorDetailFields.Name, event.target.value);
                        }}
                        onBlur={(event) => {
                            onChange(IndicatorDetailFields.Name, event.target.value);
                        }}
                    />
                    <FieldErrorList field={IndicatorDetailFields.Name} errors={fieldErrors} />
                </FormField>
                <FormField className="input-form-field">
                    <Label className="input-value-type-label" text="Indicator Value type"></Label>
                    <Select
                        className="input-value-type-value"
                        disabled
                        value={data.valueType}
                        options={INDICATOR_TYPE_OPTIONS}
                        onChange={onValueTypeChange}></Select>
                    <FieldErrorList field={IndicatorDetailFields.ValueType} errors={fieldErrors} />
                    {isShowUpdateTypeAlert && (
                        <div className="override-wrapper">
                            <Checkbox
                                id={'chkAlertChangeType'}
                                checked={isOverride}
                                name={'chkAlertChangeType'}
                                value={'override'}
                                onChange={() => {
                                    setOverride(!isOverride);
                                }}>
                                {INDICATOR_VALUE_TYPE_OVERRIDE}
                            </Checkbox>
                        </div>
                    )}
                </FormField>
                <FormField className="input-form-field">
                    <Search
                        className="search-input"
                        value={sourceSearchValue}
                        id="source-list"
                        aria-label="Search"
                        placeholder="Source (required)"
                        items={getSourceListOptions()}
                        onChange={(event) => onSourceSearchChange(event.target.value)}
                        onSelect={onSourceSelection}
                    />
                    <FieldErrorList field={IndicatorDetailFields.SourceId} errors={fieldErrors} />
                    <span
                        className={
                            isSetUpIndicatorSourceDisabled
                                ? 'add-new-source-disabled'
                                : 'add-new-source'
                        }
                        onClick={() =>
                            isSetUpIndicatorSourceDisabled ? '' : handleNewSourceAddition()
                        }>
                        Add new source
                    </span>
                </FormField>
                <span
                    className={'input-web-address-container'}
                    title={selectedWebAddress?.label || ''}>
                    <FormField className="input-form-field">
                        <Label className="input-web-address-label" text="Web address" />
                        <Select
                            value={selectedWebAddress?.value || ''}
                            options={webAddressOptions}
                            onChange={function (selectedValue: any): void {
                                onChange(IndicatorDetailFields.WebAddress, selectedValue);
                            }}
                        />
                        <FieldErrorList
                            field={IndicatorDetailFields.WebAddress}
                            errors={fieldErrors}
                        />
                    </FormField>
                </span>
                <FormField className="input-form-field">
                    <Label text="Update Frequency (required)"></Label>
                    <Select
                        value={data.frequencyTypeId}
                        options={INDICATOR_UPDATE_FREQUENCY}
                        onChange={function (selectedValue: any): void {
                            onChange(IndicatorDetailFields.FrequencyTypeId, selectedValue);
                        }}
                    />
                    <FieldErrorList
                        field={IndicatorDetailFields.FrequencyTypeId}
                        errors={fieldErrors}
                    />
                </FormField>
                <FormField className="input-form-field">
                    <DatePicker
                        label="Source Date (required)"
                        className="session-date-picker"
                        showLeadingZeros={true}
                        value={data.sourceDate}
                        onChange={(val: any) => onChange(IndicatorDetailFields.SourceDate, val)}
                        format={
                            data.frequencyTypeId == '1e001a8f-ea9a-43b3-a3cf-0e2e4468c747' // 'Annually'
                                ? 'yyyy'
                                : data.frequencyTypeId == 'e575c582-8036-4e05-aac2-38265ea36322' || // 'Quarterly' ||
                                  data.frequencyTypeId == '10e55757-bc68-401e-9f5c-6a1944e64ced' // 'Monthly'
                                ? 'MM/yyyy'
                                : 'dd/MM/yyyy'
                        }
                        yearPlaceholder="YYYY"
                        dayPlaceholder="DD"
                        monthPlaceholder="MM"
                    />
                    <FieldErrorList field={IndicatorDetailFields.SourceDate} errors={fieldErrors} />
                </FormField>
                <FormField className="input-form-field">
                    <Label text="Indicator Order (required)"></Label>
                    <Input
                        type={InputType.TEXT}
                        value={data.order}
                        onChange={(event) => {
                            onChange(IndicatorDetailFields.Order, event.target.value);
                        }}
                        onBlur={(event) => {
                            onChange(IndicatorDetailFields.Order, event.target.value);
                        }}
                    />
                    <FieldErrorList field={IndicatorDetailFields.Order} errors={fieldErrors} />
                </FormField>
                <FormField className="input-form-field">
                    <Label text="Sort Order"></Label>
                    <Select
                        value={data.sortOrder}
                        options={INDICATOR_SORT_ORDER}
                        onChange={function (selectedValue: any): void {
                            onChange(IndicatorDetailFields.SortOrder, selectedValue);
                        }}
                    />
                </FormField>
                <FormField className="input-form-field">
                    <Label text="Description"></Label>
                    <TextArea
                        className="text-area"
                        value={data.description}
                        rows={3}
                        onChange={(event) => {
                            onChange(IndicatorDetailFields.Description, event.target.value);
                        }}></TextArea>
                    <FieldErrorList
                        field={IndicatorDetailFields.Description}
                        errors={fieldErrors}
                    />
                </FormField>
                <FormField className="input-form-field">
                    <Label text="Reference (limit to 2500 characters)"></Label>
                    <TextArea
                        value={data.reference}
                        onChange={(event) => {
                            onChange(IndicatorDetailFields.Reference, event.target.value);
                        }}
                    />
                    <FieldErrorList field={IndicatorDetailFields.Reference} errors={fieldErrors} />
                </FormField>
                <label>Data count: {dataCount?.toString()}</label>
            </div>
            {isAddSourceModalVisible && (
                <IndicatorSourceAddEdit
                    isVisible={isAddSourceModalVisible}
                    data={undefined}
                    onClose={handleAddNewClose}
                />
            )}
        </>
    );
};

export default IndicatorDetails;
